import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCamera,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiImage,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import {
  createDealerSubmission,
  deleteDealerSubmission,
  deleteDealerSubmissionPhoto,
  getCarMakes,
  getDealerSubmissions,
  getPortalDealerAccounts,
  publishOwnDealerSubmission,
  updateDealerSubmission,
  uploadDealerSubmissionPhoto,
  DealerSubmissionPayload,
  DealerSubmissionPublishPayload,
} from "api/services/dealer";
import { getDealerPortalPath, Paths } from "router/Paths";
import {
  CarMakeType,
  DealerSubmissionImageSlotType,
  DealerSubmissionStatusType,
  DealerSubmissionType,
  DealerType,
} from "types/loadedData";
import Images from "images/Images";
import {
  DealerPortalLanguage,
  getSavedDealerLanguage,
  saveDealerLanguage,
} from "copy/dealerPortalCopy";

type PageMode = "home" | "post" | "deal";
type DealerSubmissionViewStatus =
  | "All"
  | "Review"
  | "Posted"
  | "Unavailable"
  | "Rejected";

type BasicForm = {
  price: string;
  year: string;
  isDeal: boolean;
};

type DetailForm = {
  make: string;
  model: string;
  fuelType: string;
  color: string;
  batteryRangeKm: string;
  note: string;
};

const requiredPhotoSlots: {
  slot: DealerSubmissionImageSlotType;
  labelKey: "frontPhoto" | "backPhoto" | "interiorPhoto";
}[] = [
  { slot: "front", labelKey: "frontPhoto" },
  { slot: "back", labelKey: "backPhoto" },
  { slot: "interior", labelKey: "interiorPhoto" },
];

const emptyBasicForm: BasicForm = {
  price: "",
  year: "",
  isDeal: false,
};

const emptyDetails: DetailForm = {
  make: "",
  model: "",
  fuelType: "",
  color: "",
  batteryRangeKm: "",
  note: "",
};

const fuelTypeOptions = ["Gas", "Diesel", "Hybrid", "Electric"];
const yearOptions = ["2026", "2025", "2024", "2023"];

const digitsOnly = (value = "") => value.replace(/\D/g, "");
const formatPriceInput = (value = "") => {
  const digits = digitsOnly(String(value));
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
const getStoredDealer = (): DealerType | null => {
  try {
    const value = localStorage.getItem("dealer");
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getImageForSlot = (
  submission: DealerSubmissionType | null,
  slot: DealerSubmissionImageSlotType
) => submission?.images?.find((image) => image.slot === slot);

const canDealerEdit = (submission: DealerSubmissionType) =>
  ["Submitted", "Needs info", "Published", "Unavailable", "Rejected", "Sold"].includes(
    submission.status
  );

const isPostedSubmission = (submission: DealerSubmissionType) =>
  submission.status === "Published";

const isUnavailableSubmission = (submission: DealerSubmissionType) =>
  submission.status === "Unavailable" || submission.status === "Sold";

const isPublishedLikeSubmission = (submission: DealerSubmissionType) =>
  isPostedSubmission(submission) || isUnavailableSubmission(submission);

const getDealerStatusView = (
  status: DealerSubmissionStatusType
): "Review" | "Posted" | "Unavailable" | "Rejected" => {
  if (status === "Published") return "Posted";
  if (status === "Unavailable" || status === "Sold") return "Unavailable";
  if (status === "Rejected") return "Rejected";
  return "Review";
};

const getSubmissionTitle = (submission: DealerSubmissionType) =>
  submission.makeModelText ||
  (submission.publishedCar && typeof submission.publishedCar === "object"
    ? [submission.publishedCar.model, submission.publishedCar.year]
        .filter(Boolean)
        .join(" ")
    : "") ||
  [submission.fuelType, submission.year].filter(Boolean).join(" ") ||
  "Submitted car";

const formatDate = (value?: string) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const dealerText = (language: DealerPortalLanguage) =>
  language === "am"
    ? {
        languageToggle: "En",
        logout: "ውጣ",
        appName: "BossCar",
        homeTitle: "መኪና ለመላክ",
        homeSubtitle: "ፎቶ፣ ዋጋ እና አመት ያስገቡ።",
        newPost: "የቅናሽ መኪና ላክ",
        search: "የላኩትን መኪና ይፈልጉ",
        myCars: "የላኩት መኪናዎች",
        postTitle: "መኪና ላክ",
        photos: "ፎቶዎች",
        chooseGallery: "ፎቶዎችን ከጋለሪ ይምረጡ",
        frontPhoto: "ፊት",
        backPhoto: "ኋላ",
        interiorPhoto: "ውስጥ",
        price: "ዋጋ",
        year: "የተሰራበት አመት",
        fullDetails: "ሙሉ መረጃ እራስዎ ያክሉ",
        send: "ወደ BossCar ይላኩ",
        post: "መኪናውን ፖስት ያድርጉ",
        sentTitle: "ተልኳል",
        sentBody: "BossCar ይመለከተዋል።",
        postedBody: "መኪናው በቀጥታ ተፖስቷል።",
        missingPhotos: "3 ፎቶዎችን ያክሉ።",
        priceRequired: "ዋጋ ያስገቡ።",
        yearRequired: "አመት ያስገቡ።",
        completeDetails: "ሙሉ መረጃውን ያሟሉ።",
        empty: "እስካሁን መኪና አልተላከም።",
      }
    : {
        languageToggle: "አማ",
        logout: "Logout",
        appName: "BossCar",
        homeTitle: "Send your car to BossCar",
        homeSubtitle: "Add photos, price, and year.",
        newPost: "Post a discount deal",
        search: "Search sent cars",
        myCars: "My sent cars",
        postTitle: "Post a car",
        photos: "Photos",
        chooseGallery: "Choose photos from gallery",
        frontPhoto: "Front",
        backPhoto: "Back",
        interiorPhoto: "Interior",
        price: "Price",
        year: "Year of Manufacture",
        fullDetails: "Add full details yourself",
        send: "Send to BossCar",
        post: "Post car",
        sentTitle: "Sent",
        sentBody: "BossCar will review and finish the post.",
        postedBody: "Your car was posted directly.",
        missingPhotos: "Add 3 photos.",
        priceRequired: "Enter price.",
        yearRequired: "Enter year.",
        completeDetails: "Complete all full details to post directly.",
        empty: "No sent cars yet.",
      };

function DealerInventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState<DealerPortalLanguage>(
    getSavedDealerLanguage()
  );
  const [dealer, setDealer] = useState<DealerType | null>(getStoredDealer);
  const [submissions, setSubmissions] = useState<DealerSubmissionType[]>([]);
  const [carMakes, setCarMakes] = useState<CarMakeType[]>([]);
  const [mode, setMode] = useState<PageMode>("home");
  const [basicForm, setBasicForm] = useState<BasicForm>(emptyBasicForm);
  const [detailForm, setDetailForm] = useState<DetailForm>(emptyDetails);
  const [files, setFiles] = useState<
    Partial<Record<DealerSubmissionImageSlotType, File>>
  >({});
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [editingSubmission, setEditingSubmission] =
    useState<DealerSubmissionType | null>(null);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);
  const [submissionFilter, setSubmissionFilter] =
    useState<DealerSubmissionViewStatus>("All");
  const [submissionSearch, setSubmissionSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [dealerSearch, setDealerSearch] = useState("");
  const [dealerOptions, setDealerOptions] = useState<DealerType[]>([]);
  const [selectedDealer, setSelectedDealer] = useState<DealerType | null>(null);
  const [dealerSearching, setDealerSearching] = useState(false);

  const copy = dealerText(language);
  const startsInDealMode = useMemo(
    () => new URLSearchParams(location.search).get("deal") === "1",
    [location.search]
  );
  const isDealMode = mode === "deal";
  const priceDigits = digitsOnly(String(basicForm.price || ""));
  const yearDigits = digitsOnly(String(basicForm.year || ""));
  const selectedMake = carMakes.find((make) => make._id === detailForm.make);

  const missingSlots = useMemo(
    () =>
      requiredPhotoSlots
        .map((item) => item.slot)
        .filter(
          (slot) =>
            !files[slot] && !getImageForSlot(editingSubmission, slot)
        ),
    [editingSubmission, files]
  );

  const selectedPhotoSlots = useMemo(
    () =>
      requiredPhotoSlots.filter(
        ({ slot }) => files[slot] || getImageForSlot(editingSubmission, slot)
      ),
    [editingSubmission, files]
  );

  const directDetailsComplete = Boolean(
    detailForm.make &&
      detailForm.model.trim() &&
      detailForm.fuelType
  );
  const isPortalAdmin = dealer?.portalRole === "Admin";
  const postingDealer = isPortalAdmin ? selectedDealer : dealer;
  const dealerCanDirectPost = Boolean(
    postingDealer?.isActive &&
      (isPortalAdmin || postingDealer?.trustedDealer)
  );
  const shouldDirectPost = dealerCanDirectPost && directDetailsComplete;
  const hasDealImage =
    Boolean(files.front) || Boolean(getImageForSlot(editingSubmission, "front"));
  const canSubmit = isDealMode
    ? hasDealImage && Boolean(priceDigits) && Boolean(yearDigits) && directDetailsComplete && !saving
    : missingSlots.length === 0 && Boolean(priceDigits) && Boolean(yearDigits) && !saving;

  const filteredSubmissions = useMemo(() => {
    const query = submissionSearch.trim().toLowerCase();
    return submissions.filter((submission) => {
      const statusMatches =
        submissionFilter === "All" ||
        getDealerStatusView(submission.status) === submissionFilter;
      if (!statusMatches) return false;
      if (!query) return true;

      const searchableText = [
        submission.makeModelText,
        submission.year,
        submission.fuelType,
        submission.price,
        submission.note,
        submission.staffNotes,
        submission.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [submissionFilter, submissionSearch, submissions]);

  useEffect(() => {
    const token = localStorage.getItem("dealerToken");
    if (!token) {
      const next = `${location.pathname}${location.search}`;
      navigate(
        `${getDealerPortalPath(Paths.dealerLogin)}?next=${encodeURIComponent(next)}`
      );
      return;
    }
    setDealer(getStoredDealer());
    if (startsInDealMode) {
      setBasicForm({
        ...emptyBasicForm,
        isDeal: true,
      });
      setDetailForm(emptyDetails);
      setFiles({});
      setExtraFiles([]);
      setEditingSubmission(null);
      setShowOptionalDetails(true);
      setMode("deal");
    }
    loadSubmissions();
    loadCarMakes();
  }, [location.pathname, location.search, navigate, startsInDealMode]);

  useEffect(() => {
    if (!isPortalAdmin) return;
    const timer = window.setTimeout(async () => {
      setDealerSearching(true);
      try {
        setDealerOptions(await getPortalDealerAccounts(dealerSearch));
      } catch (error: any) {
        setMessage(error?.response?.data?.msg || "Unable to search importers.");
      } finally {
        setDealerSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [dealerSearch, isPortalAdmin]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      setSubmissions(await getDealerSubmissions());
    } catch (error: any) {
      setMessage(error?.response?.data?.msg || "Unable to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  const loadCarMakes = async () => {
    try {
      setCarMakes(await getCarMakes());
    } catch {
      setCarMakes([]);
    }
  };

  const toggleLanguage = () => {
    const nextLanguage = language === "en" ? "am" : "en";
    setLanguage(nextLanguage);
    saveDealerLanguage(nextLanguage);
  };

  const handleLogout = () => {
    localStorage.removeItem("dealerToken");
    localStorage.removeItem("dealer");
    navigate(getDealerPortalPath(Paths.dealerLogin));
  };

  const resetForm = () => {
    setBasicForm(emptyBasicForm);
    setDetailForm(emptyDetails);
    setFiles({});
    setExtraFiles([]);
    setEditingSubmission(null);
    setShowOptionalDetails(false);
    setMessage("");
  };

  const goHome = () => {
    resetForm();
    setMode("home");
  };

  const startPost = () => {
    if (isPortalAdmin) {
      resetForm();
      setShowOptionalDetails(true);
      setMode("post");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate(getDealerPortalPath(Paths.dealerDealPost));
  };

  const updateBasic = (field: "price" | "year", value: string) => {
    setMessage("");
    setSuccessMessage("");
    if (field === "price") {
      setBasicForm((current) => ({ ...current, price: formatPriceInput(value) }));
      return;
    }
    setBasicForm((current) => ({ ...current, [field]: digitsOnly(value).slice(0, 4) }));
  };

  const updateDetails = (field: keyof DetailForm, value: string) => {
    setMessage("");
    setSuccessMessage("");
    setDetailForm((current) => ({ ...current, [field]: value }));
  };

  const handleFileChange = (
    slot: DealerSubmissionImageSlotType,
    file?: File
  ) => {
    if (!file) return;
    setFiles((current) => ({ ...current, [slot]: file }));
    setMessage("");
    setSuccessMessage("");
  };

  const handleGalleryFiles = (fileList?: FileList | null) => {
    const selectedFiles = Array.from(fileList || []);
    if (!selectedFiles.length) return;

    const nextFiles = { ...files };
    const nextExtras = [...extraFiles];
    if (isDealMode) {
      const [mainImage, ...extras] = selectedFiles;
      nextFiles.front = mainImage;
      nextExtras.push(...extras);
      setFiles(nextFiles);
      setExtraFiles(nextExtras);
      setMessage("");
      setSuccessMessage("");
      return;
    }

    selectedFiles.forEach((file) => {
      const nextMissingSlot = requiredPhotoSlots
        .map((item) => item.slot)
        .find(
          (slot) => !nextFiles[slot] && !getImageForSlot(editingSubmission, slot)
        );
      if (nextMissingSlot) {
        nextFiles[nextMissingSlot] = file;
      } else {
        nextExtras.push(file);
      }
    });
    setFiles(nextFiles);
    setExtraFiles(nextExtras);
    setMessage("");
    setSuccessMessage("");
  };

  const handleQuickCameraFile = (file?: File) => {
    if (!file) return;
    if (isDealMode) {
      handleFileChange("front", file);
      return;
    }

    const nextMissingSlot = requiredPhotoSlots
      .map((item) => item.slot)
      .find((slot) => !files[slot] && !getImageForSlot(editingSubmission, slot));

    if (nextMissingSlot) {
      handleFileChange(nextMissingSlot, file);
      return;
    }
    setExtraFiles((current) => [...current, file]);
  };

  const removeLocalFile = async (slot: DealerSubmissionImageSlotType) => {
    if (files[slot]) {
      setFiles((current) => {
        const next = { ...current };
        delete next[slot];
        return next;
      });
      return;
    }

    const image = getImageForSlot(editingSubmission, slot);
    if (!editingSubmission || !image) return;
    await deleteDealerSubmissionPhoto({
      id: editingSubmission._id,
      imageId: image._id,
    });
    const updatedSubmissions = submissions.map((submission) =>
      submission._id === editingSubmission._id
        ? {
            ...submission,
            images: submission.images.filter((item) => item._id !== image._id),
          }
        : submission
    );
    const updatedEditing = updatedSubmissions.find(
      (item) => item._id === editingSubmission._id
    );
    setSubmissions(updatedSubmissions);
    setEditingSubmission(updatedEditing || null);
  };

  const removeExtraFile = (indexToRemove: number) => {
    setExtraFiles((current) =>
      current.filter((_, index) => index !== indexToRemove)
    );
  };

  const buildSubmissionPayload = (): DealerSubmissionPayload => ({
    ...(isPortalAdmin && selectedDealer ? { dealerId: selectedDealer._id } : {}),
    makeModelText: [selectedMake?.name, detailForm.model].filter(Boolean).join(" "),
    year: yearDigits,
    fuelType: detailForm.fuelType,
    color: detailForm.color.trim() || "Unspecified",
    price: priceDigits,
    isDeal: basicForm.isDeal,
    batteryRangeKm:
      detailForm.fuelType === "Electric" ? digitsOnly(detailForm.batteryRangeKm) : "",
    note: detailForm.note,
    make: detailForm.make,
    model: detailForm.model.trim(),
  });

  const buildPublishPayload = (): DealerSubmissionPublishPayload => ({
    make: detailForm.make,
    model: detailForm.model.trim(),
    year: yearDigits,
    price: priceDigits,
    fuelType: detailForm.fuelType,
    color: detailForm.color.trim() || "Unspecified",
    isDeal: basicForm.isDeal,
    batteryRangeKm:
      detailForm.fuelType === "Electric" ? digitsOnly(detailForm.batteryRangeKm) : "",
    description: detailForm.note,
  });

  const validateSubmit = () => {
    if (isPortalAdmin && !selectedDealer) {
      return language === "am" ? "መኪናው የማን እንደሆነ አስመጪውን ይምረጡ።" : "Select the importer that owns this car.";
    }
    if (isDealMode && !hasDealImage) {
      return language === "am" ? "የመኪና ፎቶ ያክሉ።" : "Add a car image.";
    }
    if (!isDealMode && missingSlots.length) return copy.missingPhotos;
    if (!priceDigits) return copy.priceRequired;
    if (!yearDigits) return copy.yearRequired;
    if (Number(yearDigits) < 2000) {
      return language === "am"
        ? "የተሰራበት አመት 2000 ወይም ከዚያ በኋላ መሆን አለበት።"
        : "Year must be 2000 or newer.";
    }
    if (isDealMode && !directDetailsComplete) {
      return "Add brand, model, and fuel type.";
    }
    return "";
  };

  const uploadChangedPhotos = async (submission: DealerSubmissionType) => {
    for (const { slot, labelKey } of requiredPhotoSlots) {
      const file = files[slot];
      if (!file) continue;
      try {
        await uploadDealerSubmissionPhoto(submission._id, slot, file);
      } catch (error: any) {
        throw new Error(
          `${copy[labelKey]} upload failed. ${
            error?.response?.data?.msg || "Please try again."
          }`
        );
      }
    }

    for (let index = 0; index < extraFiles.length; index += 1) {
      try {
        await uploadDealerSubmissionPhoto(
          submission._id,
          "extra",
          extraFiles[index],
          index
        );
      } catch (error: any) {
        throw new Error(
          `Extra photo ${index + 1} upload failed. ${
            error?.response?.data?.msg || "Please try again."
          }`
        );
      }
    }

    if (files.deal) {
      try {
        await uploadDealerSubmissionPhoto(submission._id, "deal", files.deal);
      } catch (error: any) {
        throw new Error(
          `Deal image upload failed. ${
            error?.response?.data?.msg || "Please try again."
          }`
        );
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationMessage = validateSubmit();
    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }

    setSaving(true);
    setMessage("");
    setSuccessMessage("");
    try {
      const saved = editingSubmission
        ? await updateDealerSubmission(editingSubmission._id, buildSubmissionPayload())
        : await createDealerSubmission(buildSubmissionPayload());

      setEditingSubmission(saved);
      await uploadChangedPhotos(saved);
      const finalSubmission = shouldDirectPost
        ? await publishOwnDealerSubmission(saved._id, buildPublishPayload())
        : saved;

      await loadSubmissions();
      resetForm();
      setMode("home");
      setSuccessMessage(shouldDirectPost ? copy.postedBody : copy.sentBody);
      setSubmissions((current) =>
        current.some((item) => item._id === finalSubmission._id)
          ? current.map((item) =>
              item._id === finalSubmission._id ? finalSubmission : item
            )
          : [finalSubmission, ...current]
      );
    } catch (error: any) {
      setMessage(error?.message || error?.response?.data?.msg || "Could not send car.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (submission: DealerSubmissionType) => {
    const publishedCar =
      submission.publishedCar && typeof submission.publishedCar === "object"
        ? submission.publishedCar
        : undefined;
    const isDeal = Boolean(submission.isDeal || publishedCar?.isDeal);
    setEditingSubmission(submission);
    if (isPortalAdmin && submission.dealer && typeof submission.dealer === "object") {
      setSelectedDealer(submission.dealer);
      setDealerSearch(submission.dealer.phoneNumber || submission.dealer.name);
    }
    setFiles({});
    setExtraFiles([]);
    setSuccessMessage("");
    setMode(isDeal ? "deal" : "post");
    setShowOptionalDetails(
      Boolean(
          submission.makeModelText ||
          submission.fuelType ||
          submission.color ||
          submission.note ||
          isDeal
      )
    );
    setBasicForm({
      price: submission.price ? formatPriceInput(String(submission.price)) : "",
      year: submission.year ? String(submission.year) : "",
      isDeal,
    });
    setDetailForm({
      ...emptyDetails,
      model: submission.makeModelText || "",
      fuelType: submission.fuelType || "",
      color: submission.color || "",
      batteryRangeKm: submission.batteryRangeKm
        ? String(submission.batteryRangeKm)
        : "",
      note: submission.note || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (submission: DealerSubmissionType) => {
    const prompt = isPublishedLikeSubmission(submission)
      ? "Delete this posted car from BossCar?"
      : "Delete this submission?";
    if (!window.confirm(prompt)) return;
    await deleteDealerSubmission(submission._id);
    setSubmissions((current) =>
      current.filter((item) => item._id !== submission._id)
    );
    if (editingSubmission?._id === submission._id) goHome();
  };

  const handleAvailability = async (
    submission: DealerSubmissionType,
    available: boolean
  ) => {
    const updated = await updateDealerSubmission(submission._id, {
      status: available ? "Published" : "Unavailable",
      price: submission.price,
      year: submission.year,
    });
    setSubmissions((current) =>
      current.map((item) => (item._id === updated._id ? updated : item))
    );
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { All: submissions.length };
    submissions.forEach((submission) => {
      const status = getDealerStatusView(submission.status);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [submissions]);

  return (
    <main className={ClassName.page}>
      <section className={ClassName.shell}>
        {mode === "home" ? (
          <>
            <DealerHeader
              copy={copy}
              language={language}
              onToggleLanguage={toggleLanguage}
            />
            <div className="mx-4 mt-6 mb-5">
              <button
                type="button"
                className="flex min-h-[120px] w-full items-center justify-center gap-3 rounded-2xl bg-primary text-xl font-bold text-white shadow-sm active:scale-[0.99] transition-all hover:bg-primary/95"
                onClick={startPost}
              >
                <FiPlus className="text-2xl font-bold" />
                <span>
                  {isPortalAdmin
                    ? language === "am"
                      ? "መኪና ይለጥፉ"
                      : "Post a car"
                    : copy.newPost}
                </span>
              </button>
            </div>
            {successMessage && (
              <section className={ClassName.successCard}>
                <FiCheckCircle className={ClassName.successIcon} />
                <div>
                  <h2 className={ClassName.successTitle}>{copy.sentTitle}</h2>
                  <p className={ClassName.successText}>{successMessage}</p>
                </div>
              </section>
            )}
            {message && <p className={ClassName.message}>{message}</p>}
            <SubmissionsHome
              copy={copy}
              submissions={filteredSubmissions}
              allSubmissions={submissions}
              search={submissionSearch}
              filter={submissionFilter}
              statusCounts={statusCounts}
              loading={loading}
              onSearch={setSubmissionSearch}
              onFilter={setSubmissionFilter}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAvailability={handleAvailability}
            />
            <button type="button" className={ClassName.homeLogoutButton} onClick={handleLogout}>
              {copy.logout}
            </button>
          </>
        ) : (
          <form className={ClassName.postPage} onSubmit={handleSubmit}>
            <header className={ClassName.postHeader}>
              <button type="button" className={ClassName.backButton} onClick={goHome}>
                <FiArrowLeft />
              </button>
              <h1 className={ClassName.postTitle}>
                {isDealMode ? "Discount deal" : copy.postTitle}
              </h1>
              <button type="button" className={ClassName.langMini} onClick={toggleLanguage}>
                {copy.languageToggle}
              </button>
            </header>

            {isPortalAdmin && (
              <section className={ClassName.card}>
                <h2 className={ClassName.sectionTitle}>
                  {language === "am" ? "ለየትኛው አስመጪ ነው?" : "Post for an importer"}
                </h2>
                <p className="mb-3 text-sm text-gray-500">
                  {language === "am"
                    ? "መኪናው የማን እንደሆነ በስም ወይም በስልክ ይፈልጉ።"
                    : "Find the importer by name or phone number, then select the account."}
                </p>
                <div className="relative">
                  <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-4 text-base outline-none focus:border-gray-500"
                    value={dealerSearch}
                    onChange={(event) => {
                      setDealerSearch(event.target.value);
                      setSelectedDealer(null);
                    }}
                    placeholder={language === "am" ? "ስም ወይም ስልክ ቁጥር" : "Importer name or phone"}
                  />
                </div>
                {selectedDealer ? (
                  <div className="mt-3 flex items-center justify-between rounded-xl border border-gray-900 bg-gray-50 px-4 py-3">
                    <div>
                      <p className="font-bold text-gray-900">{selectedDealer.name}</p>
                      <p className="text-sm text-gray-500">{selectedDealer.phoneNumber}</p>
                    </div>
                    <button
                      type="button"
                      className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600"
                      onClick={() => setSelectedDealer(null)}
                    >
                      {language === "am" ? "ቀይር" : "Change"}
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 max-h-52 overflow-y-auto rounded-xl border border-gray-100 bg-white">
                    {dealerSearching ? (
                      <p className="p-4 text-sm text-gray-500">Searching...</p>
                    ) : dealerOptions.length ? (
                      dealerOptions.map((option) => (
                        <button
                          key={option._id}
                          type="button"
                          className="flex w-full items-center justify-between border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50"
                          onClick={() => {
                            setSelectedDealer(option);
                            setDealerSearch(option.phoneNumber || option.name);
                          }}
                        >
                          <span>
                            <span className="block font-semibold text-gray-900">{option.name}</span>
                            <span className="block text-sm text-gray-500">{option.phoneNumber}</span>
                          </span>
                          <span className="text-sm font-semibold text-gray-700">Select</span>
                        </button>
                      ))
                    ) : (
                      <p className="p-4 text-sm text-gray-500">
                        {language === "am" ? "አስመጪ አልተገኘም።" : "No importer found."}
                      </p>
                    )}
                  </div>
                )}
              </section>
            )}

            <section className={ClassName.card}>
              <h2 className={ClassName.sectionTitle}>
                {isDealMode
                  ? "Discount car image"
                  : language === "am"
                    ? "ቢያንስ 3 ፎቶዎች ያክሉ"
                    : "Add at least 3 Photos"}
              </h2>
              <div className={ClassName.photoActionRow}>
                <label className={`${ClassName.photoActionTile} ${ClassName.photoActionAdd}`}>
                  <FiPlus className={ClassName.photoActionPlus} />
                  <input
                    className="hidden"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) => handleGalleryFiles(event.target.files)}
                  />
                </label>
                <label className={ClassName.photoActionTile}>
                  <FiCamera className={ClassName.photoActionCamera} />
                  <span>{language === "am" ? "ፎቶ አንሳ" : "Take Photo"}</span>
                  <input
                    className="hidden"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(event) => handleQuickCameraFile(event.target.files?.[0])}
                  />
                </label>
              </div>
              {isDealMode ? (
                <div className="mt-3 max-w-[142px]">
                  <PhotoTile
                    slot="front"
                    label="Car image"
                    file={files.front}
                    existingImage={getImageForSlot(editingSubmission, "front")}
                    onFileChange={handleFileChange}
                    onRemove={() => removeLocalFile("front")}
                  />
                </div>
              ) : selectedPhotoSlots.length > 0 ? (
                <div className={ClassName.photoGrid}>
                  {selectedPhotoSlots.map(({ slot, labelKey }) => (
                    <PhotoTile
                      key={slot}
                      slot={slot}
                      label={copy[labelKey]}
                      file={files[slot]}
                      existingImage={getImageForSlot(editingSubmission, slot)}
                      onFileChange={handleFileChange}
                      onRemove={() => removeLocalFile(slot)}
                    />
                  ))}
                </div>
              ) : null}
              {extraFiles.length > 0 && (
                <div className={ClassName.extraPreviewRow}>
                  {extraFiles.map((file, index) => (
                    <div className={ClassName.extraThumb} key={`${file.name}-${index}`}>
                      <img
                        className={ClassName.fitImage}
                        src={URL.createObjectURL(file)}
                        alt={`Extra ${index + 1}`}
                      />
                      <button
                        type="button"
                        className={ClassName.extraRemove}
                        onClick={() => removeExtraFile(index)}
                      >
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className={ClassName.card}>
              <label className={ClassName.fieldLabel}>{copy.price}</label>
              <div className={ClassName.priceShell}>
                <span className={ClassName.currency}>Br</span>
                <input
                  className={ClassName.priceInput}
                  placeholder="5,500,000"
                  inputMode="numeric"
                  value={basicForm.price}
                  onChange={(event) => updateBasic("price", event.target.value)}
                />
              </div>
              <label className={ClassName.fieldLabel}>{copy.year}</label>
              <input
                className={`${ClassName.input} ${ClassName.yearInput}`}
                placeholder="2025"
                inputMode="numeric"
                list="dealer-year-options"
                value={basicForm.year}
                onChange={(event) => updateBasic("year", event.target.value)}
              />
              <datalist id="dealer-year-options">
                {yearOptions.map((year) => (
                  <option key={year} value={year} />
                ))}
              </datalist>
            </section>

            {isDealMode ? (
              <section className={ClassName.optionalCard}>
                <div className={ClassName.detailsGrid}>
                  <select
                    className={ClassName.input}
                    value={detailForm.make}
                    onChange={(event) => updateDetails("make", event.target.value)}
                  >
                    <option value="">Car Brand</option>
                    {carMakes.map((make) => (
                      <option key={make._id} value={make._id}>
                        {make.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className={ClassName.input}
                    placeholder="Model"
                    value={detailForm.model}
                    onChange={(event) => updateDetails("model", event.target.value)}
                  />
                  <select
                    className={ClassName.input}
                    value={detailForm.fuelType}
                    onChange={(event) => updateDetails("fuelType", event.target.value)}
                  >
                    <option value="">Fuel Type</option>
                    {fuelTypeOptions.map((fuel) => (
                      <option key={fuel} value={fuel}>
                        {fuel}
                      </option>
                    ))}
                  </select>
                  {detailForm.fuelType === "Electric" && (
                    <input
                      className={ClassName.input}
                      placeholder="KM range per charge"
                      inputMode="numeric"
                      value={detailForm.batteryRangeKm}
                      onChange={(event) =>
                        updateDetails(
                          "batteryRangeKm",
                          digitsOnly(event.target.value)
                        )
                      }
                    />
                  )}
                </div>
              </section>
            ) : (
              <section className={ClassName.optionalCard}>
                <button
                  type="button"
                  className={ClassName.optionalToggle}
                  onClick={() => setShowOptionalDetails((value) => !value)}
                >
                  <span className={ClassName.optionalIcon}>
                    {showOptionalDetails ? "-" : "+"}
                  </span>
                  <span className="flex-1">{copy.fullDetails}</span>
                  {showOptionalDetails ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {showOptionalDetails && (
                  <div className={ClassName.detailsGrid}>
                    <select
                      className={ClassName.input}
                      value={detailForm.make}
                      onChange={(event) => updateDetails("make", event.target.value)}
                    >
                      <option value="">Car Brand</option>
                      {carMakes.map((make) => (
                        <option key={make._id} value={make._id}>
                          {make.name}
                        </option>
                      ))}
                    </select>
                    <input
                      className={ClassName.input}
                      placeholder="Model"
                      value={detailForm.model}
                      onChange={(event) => updateDetails("model", event.target.value)}
                    />
                    <select
                      className={ClassName.input}
                      value={detailForm.fuelType}
                      onChange={(event) => updateDetails("fuelType", event.target.value)}
                    >
                      <option value="">Fuel Type</option>
                      {fuelTypeOptions.map((fuel) => (
                        <option key={fuel} value={fuel}>
                          {fuel}
                        </option>
                      ))}
                    </select>
                    <textarea
                      className={`${ClassName.input} ${ClassName.textarea}`}
                      placeholder="Description (optional)"
                      value={detailForm.note}
                      onChange={(event) => updateDetails("note", event.target.value)}
                    />
                  </div>
                )}
              </section>
            )}

            {message && <p className={ClassName.message}>{message}</p>}
            <button className={ClassName.primaryButton} disabled={!canSubmit}>
              {saving
                ? "Sending..."
                : isDealMode
                  ? shouldDirectPost
                    ? "Post discount car"
                    : "Send discount car"
                  : shouldDirectPost
                    ? copy.post
                    : copy.send}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

function DealerHeader({
  copy,
  language,
  onToggleLanguage,
}: {
  copy: ReturnType<typeof dealerText>;
  language: DealerPortalLanguage;
  onToggleLanguage: () => void;
}) {
  return (
    <header className={ClassName.topBar}>
      <div className={ClassName.brand}>
        <img src={Images.logo} alt="BossCar" className={ClassName.logo} />
      </div>
      <div className={ClassName.headerActions}>
        <button type="button" className={ClassName.textButton} onClick={onToggleLanguage}>
          {language === "en" ? copy.languageToggle : "En"}
        </button>
      </div>
    </header>
  );
}

function SubmissionsHome({
  copy,
  submissions,
  allSubmissions,
  search,
  filter,
  statusCounts,
  loading,
  onSearch,
  onFilter,
  onEdit,
  onDelete,
  onAvailability,
}: {
  copy: ReturnType<typeof dealerText>;
  submissions: DealerSubmissionType[];
  allSubmissions: DealerSubmissionType[];
  search: string;
  filter: DealerSubmissionViewStatus;
  statusCounts: Record<string, number>;
  loading: boolean;
  onSearch: (value: string) => void;
  onFilter: (value: DealerSubmissionViewStatus) => void;
  onEdit: (submission: DealerSubmissionType) => void;
  onDelete: (submission: DealerSubmissionType) => void;
  onAvailability: (submission: DealerSubmissionType, available: boolean) => void;
}) {
  const filters: DealerSubmissionViewStatus[] = [
    "All",
    "Review",
    "Posted",
    "Unavailable",
    "Rejected",
  ];

  return (
    <>
      <label className={ClassName.searchShell}>
        <FiSearch className="text-secondaryText text-lg shrink-0" />
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Toyota 2022"
          className={ClassName.searchInput}
        />
      </label>
      <div className={`${ClassName.filterRow} mx-4 mb-4 mt-0`}>
        {filters.map((status) => (
          <button
            key={status}
            type="button"
            className={`${ClassName.filterChip} ${
              filter === status ? ClassName.activeFilterChip : ""
            }`}
            onClick={() => onFilter(status)}
          >
            {status} {statusCounts[status] || 0}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="primaryLoader"></div>
        </div>
      ) : submissions.length === 0 ? (
        <p className={ClassName.emptyText}>{copy.empty}</p>
      ) : (
        <div className={ClassName.sentList}>
          {submissions.map((submission) => (
            <SubmissionRow
              key={submission._id}
              submission={submission}
              onEdit={onEdit}
              onDelete={onDelete}
              onAvailability={onAvailability}
            />
          ))}
        </div>
      )}
    </>
  );
}

function PhotoTile({
  slot,
  label,
  file,
  existingImage,
  onFileChange,
  onRemove,
}: {
  slot: DealerSubmissionImageSlotType;
  label: string;
  file?: File;
  existingImage?: { url: string };
  onFileChange: (slot: DealerSubmissionImageSlotType, file?: File) => void;
  onRemove: () => void;
}) {
  const preview = file ? URL.createObjectURL(file) : existingImage?.url || "";

  return (
    <div className={ClassName.photoTileWrap}>
      <label className={ClassName.photoTile}>
        {preview ? (
          <>
            <img className={ClassName.fitImage} src={preview} alt={label} />
            <span className={ClassName.photoScrim}></span>
          </>
        ) : (
          <FiCamera className={ClassName.emptyCamera} />
        )}
        <span className={preview ? ClassName.photoLabelFilled : ClassName.photoLabel}>
          {label}
        </span>
        <input
          className="hidden"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(event) => onFileChange(slot, event.target.files?.[0])}
        />
      </label>
      {preview && (
        <button type="button" className={ClassName.removePhotoButton} onClick={onRemove}>
          <FiX />
        </button>
      )}
    </div>
  );
}

function SubmissionRow({
  submission,
  onEdit,
  onDelete,
  onAvailability,
}: {
  submission: DealerSubmissionType;
  onEdit: (submission: DealerSubmissionType) => void;
  onDelete: (submission: DealerSubmissionType) => void;
  onAvailability: (submission: DealerSubmissionType, available: boolean) => void;
}) {
  const image =
    getImageForSlot(submission, "front") ||
    submission.images?.find((item) => item.slot !== "detail") ||
    submission.images?.[0];
  const title = getSubmissionTitle(submission);
  const publishedCar =
    submission.publishedCar && typeof submission.publishedCar === "object"
      ? submission.publishedCar
      : undefined;
  const statusView = getDealerStatusView(submission.status);

  return (
    <article className={ClassName.sentRow}>
      <div className={ClassName.sentThumb}>
        {image ? (
          <img className={ClassName.fitImage} src={image.url} alt={title} />
        ) : (
          <FiImage />
        )}
      </div>
      <div className={ClassName.sentContent}>
        <h3 className={ClassName.sentCarTitle}>{title}</h3>
        <p className={ClassName.sentPrice}>
          {submission.price
            ? Number(submission.price).toLocaleString()
            : "Price missing"}
          <span className="text-secondaryText font-normal ml-1">ETB</span>
        </p>
        {publishedCar?.tag && (
          <span className="text-xs text-secondaryText">Tag {publishedCar.tag}</span>
        )}
        <span className={ClassName.sentDate}>{formatDate(submission.updatedAt)}</span>
      </div>
      <div className={ClassName.rowActions}>
        <div className="flex gap-2">
          {canDealerEdit(submission) && (
            <button
              type="button"
              className={ClassName.iconAction}
              onClick={() => onEdit(submission)}
              title="Edit"
            >
              <FiEdit2 />
            </button>
          )}
          <button
            type="button"
            className={`${ClassName.iconAction} ${ClassName.deleteAction}`}
            onClick={() => onDelete(submission)}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
        {isPostedSubmission(submission) && (
          <button
            type="button"
            className={ClassName.smallTextAction}
            onClick={() => onAvailability(submission, false)}
          >
            Make unavailable
          </button>
        )}
        {isUnavailableSubmission(submission) && (
          <button
            type="button"
            className={ClassName.smallTextAction}
            onClick={() => onAvailability(submission, true)}
          >
            Make available
          </button>
        )}
        <span className={statusClass(submission.status)}>{statusView}</span>
      </div>
    </article>
  );
}

const statusClass = (status: string) => {
  const base = ClassName.statusPill;
  if (status === "Published") {
    return `${base} bg-[#EAF7EC] text-[#2E7D32]`;
  }
  if (status === "Unavailable" || status === "Sold") {
    return `${base} bg-gray-100 text-gray-700`;
  }
  if (status === "Rejected") {
    return `${base} bg-red-100 text-red-700`;
  }
  return `${base} bg-[#FFF4E5] text-[#B78103]`;
};

const ClassName = {
  page:
    "min-h-screen w-screen max-w-full flex-none overflow-x-hidden bg-[#202631] font-sans text-primaryText",
  shell:
    "mx-auto flex min-h-screen w-full max-w-none flex-col gap-0 overflow-hidden bg-[#e8e8e8] shadow-primaryShadow sm:max-w-[390px]",
  topBar:
    "flex min-h-[86px] items-center justify-between gap-3 bg-[#2C2B34] px-5 pb-4 pt-3 text-white rounded-b-[24px] shadow-sm",
  brand: "flex items-center gap-2",
  logo: "h-9 w-28 object-contain",
  brandText: "text-xl font-medium tracking-tight text-primaryText [&>span]:text-primary",
  headerActions: "flex items-center gap-2",
  textButton:
    "min-h-[36px] rounded-xl bg-white/10 px-4 text-sm font-semibold text-white hover:bg-white/20 active:scale-95 transition-all",
  hero: "bg-white px-5 pb-5 pt-4",
  kicker: "text-sm font-normal text-secondaryText",
  pageTitle: "mt-3 text-[23px] font-semibold leading-tight text-primaryText",
  pageSubtitle: "mt-2 text-base font-normal text-secondaryText",
  newPostButton:
    "mt-5 flex min-h-[66px] w-full items-center justify-center gap-3 rounded-md bg-primary px-5 text-base font-semibold text-white shadow-sm active:scale-[0.99]",
  successCard:
    "mx-3 mb-3 flex gap-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-4 text-green-800",
  successIcon: "mt-0.5 h-7 w-7 shrink-0",
  successTitle: "text-lg font-medium",
  successText: "mt-1 text-sm leading-5",
  homeLogoutButton:
    "mb-8 ml-auto mr-4 mt-6 flex min-h-[44px] items-center justify-center rounded-2xl bg-[#FDE8E8] px-6 text-base font-semibold text-[#E73E3E] active:scale-95 transition-all shadow-sm",
  postPage: "flex min-h-screen flex-col gap-4 bg-[#e8e8e8] pb-6",
  postHeader:
    "sticky top-0 z-10 grid min-h-[92px] grid-cols-[44px_1fr_44px] items-center rounded-b-[24px] bg-[#2C2B34] px-3 pb-3 pt-3 text-white shadow-sm",
  backButton: "flex h-11 w-11 items-center justify-center rounded-full text-2xl active:scale-95",
  postTitle: "text-center text-base font-semibold",
  langMini: "h-10 rounded-xl bg-white/10 text-sm font-semibold text-white px-3 active:scale-95",
  card: "mx-4 flex flex-col gap-1.5 bg-transparent",
  sectionTitle: "text-sm font-semibold text-primaryText mb-1",
  galleryButton:
    "mb-3 flex min-h-[54px] w-full cursor-pointer items-center justify-center gap-3 rounded-[13px] bg-primary px-4 py-3 text-base font-semibold text-white shadow-sm active:scale-[0.99]",
  galleryIcon: "h-6 w-6",
  photoActionRow: "flex items-center gap-3 mt-1",
  photoActionTile:
    "flex h-[88px] w-[88px] cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl bg-[#FDE8E8] text-xs font-semibold text-[#E73E3E] active:scale-[0.98] transition-all border border-transparent hover:bg-[#FCD7D7]",
  photoActionAdd: "text-[#E73E3E] text-3xl font-light",
  photoActionPlus: "h-9 w-9 text-[#E73E3E]",
  photoActionCamera: "h-6 w-6 text-[#E73E3E]",
  photoGrid: "grid grid-cols-3 gap-2.5 mt-3",
  photoTileWrap: "relative",
  photoTile:
    "relative flex aspect-square min-h-[88px] cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-dashed border-gray-300 bg-white text-secondaryText shadow-sm",
  emptyCamera: "h-7 w-7 text-[#626574]",
  photoLabel:
    "absolute bottom-2 left-2 right-2 text-center text-xs font-medium text-secondaryText",
  photoLabelFilled:
    "absolute bottom-2 left-2 right-2 z-[2] text-left text-xs font-semibold text-white drop-shadow",
  photoScrim:
    "absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/70 to-transparent",
  fitImage: "h-full w-full object-cover",
  removePhotoButton:
    "absolute right-1.5 top-1.5 z-[3] flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow active:scale-90",
  extraPreviewRow: "mt-3 flex flex-wrap items-center gap-2",
  extraThumb: "relative h-16 w-16 overflow-hidden rounded-xl bg-bgSecondary border border-borderPrimary/20",
  extraRemove:
    "absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-primary",
  fieldLabel: "text-sm font-semibold text-primaryText mb-1 mt-2",
  priceShell:
    "flex min-h-[56px] items-center rounded-2xl border border-borderPrimary bg-white px-3 focus-within:border-primary shadow-sm",
  priceInput:
    "min-w-0 flex-1 bg-transparent text-base font-normal outline-none placeholder:text-base placeholder:font-normal placeholder:text-secondaryText",
  currency:
    "mr-3 flex h-11 w-11 items-center justify-center rounded-xl bg-bgSecondary text-sm font-semibold text-secondaryText",
  input:
    "min-h-[56px] rounded-2xl border border-borderPrimary bg-white px-4 py-3 text-base font-normal outline-none placeholder:font-normal focus:border-primary shadow-sm",
  yearInput: "w-full",
  optionalCard: "mx-4 mt-5 rounded-2xl border border-borderPrimary/25 bg-white shadow-sm overflow-hidden",
  optionalToggle:
    "flex min-h-[56px] w-full items-center gap-3 px-4 text-left text-base font-semibold text-primaryText hover:bg-bgSecondary active:scale-[0.99] transition-all",
  optionalIcon:
    "flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 text-lg font-medium text-secondaryText",
  detailsGrid: "grid grid-cols-1 gap-3 border-t border-borderPrimary p-4 sm:grid-cols-2",
  textarea: "min-h-[92px] resize-y sm:col-span-2",
  primaryButton:
    "mx-4 mt-8 mb-8 min-h-[56px] rounded-2xl bg-primary px-5 py-4 text-lg font-bold text-white shadow-sm active:scale-[0.99] transition-all hover:bg-primary/95 disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none",
  message: "mx-3 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-primary",
  submissionsCard:
    "mx-3 mb-3 rounded-[18px] border border-borderPrimary bg-white p-4 shadow-sm",
  sentHeader: "flex items-center justify-between gap-3",
  sentTitle: "text-xl font-medium text-primaryText",
  countPill:
    "flex h-8 min-w-8 items-center justify-center rounded-full bg-bgSecondary px-3 text-sm font-medium",
  searchShell:
    "mx-4 mb-5 flex min-h-[56px] items-center gap-3 rounded-[16px] bg-white px-4 text-secondaryText shadow-sm border border-borderPrimary/30",
  searchInput:
    "min-w-0 flex-1 bg-transparent text-base outline-none text-primaryText placeholder:text-secondaryText/85",
  filterRow:
    "mt-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none]",
  filterChip:
    "flex shrink-0 items-center gap-2 rounded-xl bg-bgSecondary px-4 py-2 text-sm font-normal text-primaryText",
  activeFilterChip: "bg-primary text-white",
  sentList: "mx-4 flex flex-col gap-3 pb-6",
  sentRow: "bg-white rounded-2xl p-3 grid grid-cols-[68px_1fr_auto] gap-3.5 shadow-sm border border-borderPrimary/25",
  sentThumb:
    "h-[68px] w-[68px] rounded-xl bg-bgSecondary overflow-hidden shrink-0 flex items-center justify-center border border-borderPrimary/20",
  sentContent: "min-w-0 flex flex-col justify-between py-0.5",
  sentCarTitle: "font-bold text-sm text-primaryText truncate",
  sentPrice: "text-sm font-bold text-primaryText",
  sentMetaRow: "mt-1 flex flex-wrap items-center gap-2",
  sentDate: "text-xs text-secondaryText mt-0.5 block",
  rowActions: "flex flex-col items-end justify-between min-h-[68px]",
  iconAction:
    "flex h-8 w-8 items-center justify-center rounded-lg border border-borderPrimary bg-white text-base text-[#626574] hover:bg-bgSecondary active:scale-95 transition-all",
  deleteAction: "text-[#626574]",
  smallTextAction: "rounded-xl bg-bgSecondary px-3 py-2 text-xs font-medium text-primary",
  statusPill: "rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide shrink-0",
  statusSubmitted: "bg-yellow-100 text-yellow-800",
  statusNeedsInfo: "bg-orange-100 text-orange-800",
  statusPublished: "bg-green-100 text-green-700",
  statusMuted: "bg-gray-100 text-gray-600",
  emptyText: "mt-4 text-center text-sm font-normal text-secondaryText",
};

export default DealerInventoryPage;
